import { supabase } from '@/integrations/supabase/client';
import type { MetodoEnvioEmail } from '../../../backend/src/services/leads/proposta-email-texto';

/** O que a tela manda ao servidor para montar a Proposta E-mail (prévia e envio usam o mesmo). */
export interface DadosEmailProposta {
  lead_id: string;
  razao_social?: string;
  contato_nome?: string;
  comparativo: Array<{ rotulo: string; atual: string | null; novo: string | null }>;
  valor_atual: number;
  valor_novo: number;
  servicos: Array<{ nome: string; licencas: number; valorUnitario: number; linhaVinculada?: string | null }>;
  beneficios: string[];
}

export interface PreviaPropostaEmail {
  /** O e-mail inteiro, com as marcas onde entram a abertura, o fechamento e a imagem. */
  html: string;
  assunto_padrao: string;
  abertura_padrao: string;
  fechamento_padrao: string;
  consultor: { nome: string | null; email: string | null };
  sendgrid: { from: string };
  smtp: { disponivel: boolean; email: string | null };
  copia: { nome: string | null; email: string } | null;
  /** Lead CNPJ com proposta do TOP: o e-mail saiu dela, no modelo pedido. */
  top?: { fonte: string; top: string; top_nome: string; modelo: 'previa' | 'natural' } | null;
}

export interface RespostaEnvioPropostaEmail {
  metodo: MetodoEnvioEmail;
  enviados: string[];
  falhas: Array<{ email: string; erro: string }>;
  parciais: Array<{ email: string; erro: string }>;
  copia_supervisor: string | null;
  responder_para: string | null;
  com_imagem: boolean;
}

// ⛔ O caminho relativo '/api/v1/functions/...' cai no domínio do SITE, que não
// atende a API e responde 405 — foi assim que a Proposta E-mail nunca saiu
// (zero envios até 05/10/2026). A API mora no VITE_SUPABASE_URL, como em leadSmsApi.
const BASE = () => ((import.meta.env.VITE_SUPABASE_URL as string) ?? 'https://beta.crm-api.primetecia.com.br/api').replace(/\/$/, '');

async function postarFuncao<T>(nome: string, corpo: unknown): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sessão expirada — faça login novamente.');
  const resp = await fetch(`${BASE()}/v1/functions/${nome}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify(corpo),
  });
  const dados = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(dados?.error || `Falha (${resp.status})`);
  return dados as T;
}

export function buscarPreviaPropostaEmail(dados: DadosEmailProposta & { com_imagem: boolean; modelo?: 'previa' | 'natural' }) {
  return postarFuncao<PreviaPropostaEmail>('previa-proposta-email-lead', dados);
}

export function enviarPropostaEmailLead(dados: DadosEmailProposta & {
  destinatarios: string[];
  imagem_path: string | null;
  metodo: MetodoEnvioEmail;
  assunto?: string;
  abertura_texto?: string;
  fechamento_texto?: string;
  modelo?: 'previa' | 'natural';
}) {
  return postarFuncao<RespostaEnvioPropostaEmail>('enviar-proposta-email-lead', dados);
}
